import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHoverImage } from '../../common/Layout/Layout';
import { useTransition } from '../../common/Transition/TransitionContext';
import { useView } from '../../common/View/ViewContext';
// Notionデータ取得用フック（画像プリロード付き）
import { useProjectListWithPreload } from '../../../hooks/useNotionProjectsWithPreload';
import './Home.css';

const Home: React.FC = () => {
  const { setHoveredImage } = useHoverImage();
  const { startTransition, isTransitioning } = useTransition();
  const { currentView, isTransitioning: isViewTransitioning } = useView();

  // Notion からプロジェクト一覧を取得（画像プリロード付き）
  const { projects, loading, error } = useProjectListWithPreload();

  // モバイル用：中央に表示されているプロジェクトの画像
  const [centerProjectImage, setCenterProjectImage] = useState<string | null>(null);

  // ホームページのフェードイン処理
  useEffect(() => {
    const homeElement = document.querySelector('.home');
    if (homeElement) {
      homeElement.classList.add('page-transitioning');
      setTimeout(() => {
        homeElement.classList.remove('page-transitioning');
      }, 100);
    }
  }, []);

  // 動的フォントサイズ調整は削除（CSSのclamp()を使用）

  // 初期スクロール位置を中央に設定（上下にスクロール可能にする）
  useEffect(() => {
    if (currentView !== 'title' || loading || projects.length === 0) {
      return;
    }

    const scrollContainer = document.querySelector('.content-window') as HTMLElement;
    if (!scrollContainer) return;

    // DOMが完全にレンダリングされるまで待つ
    const timer = setTimeout(() => {
      const projectItems = document.querySelectorAll('.home.title .project-item');
      if (projectItems.length === 0) return;

      // プロジェクト数を動的に取得（projectsまたはsampleProjects）
      const currentProjects = projects.length > 0 ? projects : sampleProjects;
      const itemsPerSet = currentProjects.length;

      let setHeight = 0;
      for (let i = 0; i < itemsPerSet && i < projectItems.length; i++) {
        setHeight += (projectItems[i] as HTMLElement).offsetHeight;
      }

      // 中央のセット(8番目のセット = 7セット分)の位置までスクロール
      const centerScrollPosition = setHeight * 7;
      scrollContainer.scrollTop = centerScrollPosition;

      console.log('📍 Initial scroll position set to center:', centerScrollPosition);
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, projects, loading]);

  // モバイル用：画像プリロード（積極的にキャッシュ）
  useEffect(() => {
    // モバイルサイズでない、またはプロジェクトがない場合は何もしない
    if (window.innerWidth > 768 || loading || projects.length === 0) {
      return;
    }

    // 全プロジェクトの画像を強制的にプリロード
    console.log('🖼️ Aggressively preloading', projects.length, 'images for mobile...');
    const preloadPromises = projects.map((project) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          console.log('✅ Loaded:', project.title);
          resolve(img);
        };
        img.onerror = () => {
          console.warn('❌ Failed to load:', project.title);
          resolve(null);
        };
        // 強制的にキャッシュを使わせる
        img.crossOrigin = 'anonymous';
        img.src = project.image;
      });
    });

    Promise.all(preloadPromises).then(() => {
      console.log('✨ All images preloaded!');
    });
  }, [projects, loading]);

  // モバイル用：中央のプロジェクトを検出して画像表示
  useEffect(() => {
    // TITLEビューでない、ローディング中、またはプロジェクトがない場合は何もしない
    if (currentView !== 'title' || loading || projects.length === 0) {
      setCenterProjectImage(null);
      // 既存のcenter-activeクラスをクリア
      document.querySelectorAll('.home.title .project-item.center-active').forEach(item => {
        item.classList.remove('center-active');
      });
      return;
    }

    // モバイルサイズ（768px以下）の時のみ監視を有効化
    const isMobile = window.innerWidth <= 768;
    console.log('🔍 Mobile detection - innerWidth:', window.innerWidth, 'isMobile:', isMobile, 'projects:', projects.length);

    if (!isMobile) {
      setCenterProjectImage(null);
      document.querySelectorAll('.home.title .project-item.center-active').forEach(item => {
        item.classList.remove('center-active');
      });
      return;
    }

    // DOMが準備できるまで複数回リトライ
    let retryCount = 0;
    const maxRetries = 5;
    let observer: IntersectionObserver | null = null;

    const setupObserver = () => {
      const projectItems = document.querySelectorAll('.home.title .project-item');
      console.log(`📦 Attempt ${retryCount + 1}: Found project items:`, projectItems.length);

      if (projectItems.length === 0 && retryCount < maxRetries) {
        retryCount++;
        setTimeout(setupObserver, 200);
        return;
      }

      if (projectItems.length === 0) {
        console.log('⚠️ No project items found after', maxRetries, 'retries');
        return;
      }

      const observerOptions = {
        root: null,
        rootMargin: '-45% 0px -45% 0px',
        threshold: 0.5
      };

      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log('✅ Project entered center');
            const currentActive = document.querySelector('.home.title .project-item.center-active');
            if (currentActive && currentActive !== entry.target) {
              currentActive.classList.remove('center-active');
            }

            entry.target.classList.add('center-active');

            const imageUrl = entry.target.getAttribute('data-image');
            console.log('🖼️ Setting center image:', imageUrl);
            if (imageUrl) {
              setCenterProjectImage(imageUrl);
            }
          } else {
            entry.target.classList.remove('center-active');
          }
        });
      }, observerOptions);

      console.log('👀 Starting to observe', projectItems.length, 'items');
      projectItems.forEach((item) => observer!.observe(item));
    };

    // 初回実行
    const timer = setTimeout(setupObserver, 100);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [currentView, projects, loading]);

  // サンプルプロジェクト（フォールバック用）
  const sampleProjects = [
    { 
      id: "gotoda-wedding",
      title: "Gotoda Wedding Movie", 
      category: "movie",
      image: "https://picsum.photos/120/120?random=1"
    },
    { 
      id: "cherish",
      title: "Cherish", 
      category: "mobile",
      image: "https://picsum.photos/120/120?random=2"
    },
    { 
      id: "portfolio",
      title: "Portfolio Website", 
      category: "web",
      image: "https://picsum.photos/120/120?random=3"
    },
  ];

  // プロジェクトクリックハンドラー
  const handleProjectClick = async (
    event: React.MouseEvent,
    projectId: string
  ) => {
    event.preventDefault();

    if (isTransitioning || isViewTransitioning) {
      return;
    }

    setHoveredImage(null);

    try {
      await startTransition(`/project/${projectId}`);
    } catch (error) {
      // エラーハンドリング
    }
  };

  // ローディング表示
  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: '#ffffff',
        fontFamily: 'Azeret Mono',
        fontSize: '14px',
        letterSpacing: '1px',
        zIndex: 9999
      }}>
        Loading projects...
      </div>
    );
  }

  // エラー表示（デバッグ情報付き）
  if (error) {
    return (
      <div className="home error">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100vh',
          color: '#ffffff',
          fontFamily: 'Azeret Mono',
          fontSize: '14px',
          letterSpacing: '1px',
          gap: '20px',
          padding: '40px'
        }}>
          <div>❌ Failed to load projects from Notion</div>
          <div style={{ fontSize: '12px', opacity: 0.7, textAlign: 'center' }}>
            Error: {error.message}
          </div>
          <div style={{ fontSize: '11px', opacity: 0.5, textAlign: 'center' }}>
            Check:<br/>
            1. .env file exists with correct tokens<br/>
            2. Notion integration has access to database<br/>
            3. Browser console (F12) for detailed errors
          </div>
          <div style={{ fontSize: '12px', marginTop: '20px' }}>
            Using sample data instead...
          </div>
        </div>
      </div>
    );
  }

  // データ取得成功の表示
  const displayProjects = projects.length > 0 ? projects : sampleProjects;


  // 無限スクロール用に15セット繰り返し（大量レンダリング方式）
  // 参考サイト(emelecollab.com)は24プロジェクト×3セット=72個
  // 同等の長さにするため: 5プロジェクト×15セット=75個
  const repeatedProjects = Array.from({ length: 15 }, (_, setIndex) =>
    displayProjects.map(p => ({ ...p, setIndex: String(setIndex + 1) }))
  ).flat();

  // プロジェクトホバー時の処理（画像プリフェッチ付き）
  const handleProjectHover = async (project: any) => {
    if (!isTransitioning && !isViewTransitioning) {
      setHoveredImage(project.image);
      
      // プロジェクトの詳細画像をプリフェッチ（IDでプロジェクトデータを取得する必要がある）
      // TODO: プロジェクトの詳細データを取得してプリフェッチ
    }
  };

  // TITLEビューのレンダリング
  const renderTitleView = () => (
    <>
      {repeatedProjects.map((project, index) => (
        <div
          key={`title-${project.setIndex}-${index % displayProjects.length}`}
          className="project-item"
          data-image={project.image}
        >
          <Link
            to={`/project/${project.id}`}
            className={`project-text ${isTransitioning || isViewTransitioning ? 'disabled' : ''}`}
            onMouseEnter={() => handleProjectHover(project)}
            onMouseLeave={() => !isTransitioning && !isViewTransitioning && setHoveredImage(null)}
            onClick={(e) => handleProjectClick(e, project.id)}
          >
            <h2 className="project-title">{project.title}</h2>
            <p className="project-category">
              {Array.isArray(project.category)
                ? project.category.join(', ')
                : project.category}
            </p>
          </Link>
        </div>
      ))}
    </>
  );

  // FILMビューのレンダリング
  const renderFilmView = () => (
    <>
      {repeatedProjects.map((project, index) => (
        <div
          key={`film-${project.setIndex}-${index % displayProjects.length}`}
          className="film-item"
          data-image={project.image}
        >
          <Link
            to={`/project/${project.id}`}
            className={`film-image-container ${isTransitioning || isViewTransitioning ? 'disabled' : ''}`}
            onClick={(e) => handleProjectClick(e, project.id)}
            title={`${project.title} - ${Array.isArray(project.category) ? project.category.join(', ') : project.category}`}
          >
            <img
              src={project.image}
              alt={`${project.title} - ${Array.isArray(project.category) ? project.category.join(', ') : project.category}`}
              className="film-image"
            />
            <div className="film-text-overlay">
              <h3 className="film-text-title">{project.title}</h3>
              <p className="film-text-category">
                {Array.isArray(project.category)
                  ? project.category.join(', ')
                  : project.category}
              </p>
            </div>
          </Link>
        </div>
      ))}
    </>
  );

  return (
    <>
      <div className={`home ${currentView}`}>
        {/* メインコンテンツ */}
        {currentView === 'title' && renderTitleView()}
        {currentView === 'film' && renderFilmView()}
      </div>

      {/* モバイル用：右下固定画像表示（全画像を事前レンダリング） */}
      {currentView === 'title' && displayProjects.length > 0 && (
        <div className="mobile-preview-image">
          {displayProjects.map((project) => (
            <img
              key={project.id}
              src={project.image}
              alt={project.title}
              className={centerProjectImage === project.image ? 'active' : ''}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default Home;